import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { parsePhoneNumber } from "awesome-phonenumber";

dayjs.extend(customParseFormat);

export const validateOIB = (str: string, isOptional: boolean = false): boolean => {
  if (isOptional && str === '') {
    return true;
  }
  if (str.length !== 11 || Number.isNaN(parseInt(str, 10))) {
    return false
  }
  const digits = str.split('').map(Number);
  const controlNumber = digits.pop();
  const a = digits.reduce((acc, digit) => (((acc + digit) % 10 || 10) * 2) % 11, 10);
  const calculatedControlNumber = 11 - a === 10 ? 0 : 11 - a;
  return controlNumber === calculatedControlNumber;
};

export const isNumberValid = (value: string, regionCode: string) => {
  try {
    return parsePhoneNumber(value, { regionCode }).valid;
  } catch (_) {
    return false;
  }
};

const validateBirthDate = (dateStr: string): boolean => {
  const date = dayjs(dateStr, 'YYYY-MM-DD', true);
  if (!date.isValid()) return false;
  
  const today = dayjs();
  const minDate = today.subtract(100, 'year');
  const maxDate = today.subtract(15, 'year');
  
  return date.isAfter(minDate) && date.isBefore(maxDate);
};

export const validateFutureDate = (dateStr: string): boolean => {
  const date = dayjs(dateStr, 'YYYY-MM-DD', true);
  if (!date.isValid()) return false;
  
  const today = dayjs().startOf('day');
  return date.isAfter(today);
};

export const validateDateBiggerThan = (dateStr: string, dateStr2: string): boolean => {
  const date1 = dayjs(dateStr, 'YYYY-MM-DD', true);
  const date2 = dayjs(dateStr2, 'YYYY-MM-DD', true);
  if (!date1.isValid() || !date2.isValid()) return false;
  return date1.isAfter(date2);
};

export const checkIfOlderThan18 = (birthDate: string): boolean => {
  const today = dayjs();
  const birth = dayjs(birthDate, 'YYYY-MM-DD');
  if (!birth.isValid()) return false;
  const age = today.diff(birth, 'year');
  return age >= 18;
};

export const checkIfNotOlderThan = (birthDate: string, maxAge: number): boolean => {
  const today = dayjs();
  const birth = dayjs(birthDate, 'YYYY-MM-DD', true);
  if (!birth.isValid()) return false;
  const age = today.diff(birth, 'year');
  return age <= maxAge;
};

const validateNotLargerThan = (value: any, params?: { [key: string]: any }): boolean => {
  if (value === undefined || value === null || value === '') return true;
  const numValue = typeof value === 'number' ? value : parseInt(value, 10);
  const maxValue = params?.maxValue;
  if (maxValue === undefined || maxValue === null || maxValue === '') return true;
  const numMaxValue = typeof maxValue === 'number' ? maxValue : parseInt(maxValue, 10);
  return !isNaN(numValue) && !isNaN(numMaxValue) && numValue <= numMaxValue;
};

// Konstanta koja mapira vrste validacija na funkcije za provjeru
export const validationRules: { [key: string]: (value: any, params?: { [key: string]: any }) => boolean } = {
  required: (value: any) => {
    if (value === false) return true;
    if (typeof value === 'string') return !!value.trim();
    return !!value;
  },
  /** For checkboxes: value must be exactly true (user must check the box). */
  requiredTrue: (value: any) => value === true,
  validMail: (value: any) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()),
  validHouseNumber: (value: any) => /^.{0,5}$/.test(value.trim()),
  validFullName: (value: any) => value.trim().split(' ').length >= 2,
  validDate: (value: any) => dayjs(value, 'YYYY-MM-DD', true).isValid(), 
  validBirthDate: (value: any) => validateBirthDate(value),
  futureDate: (value: any) => validateFutureDate(value),
  validOIB: (value: any, params?: { [key: string]: any }) => validateOIB(value ? value.trim() : '', params?.optional),
  validPhoneNumber: (value: any, params?: { [key: string]: any }) => isNumberValid(value, params?.regionCode),
  validOlderThan18: (value: any) => checkIfOlderThan18(value),
  validNotOlderThan: (value: any, params?: { [key: string]: any }) => {
    const maxAge = params?.maxAge;
    if (maxAge === undefined || maxAge === null) return true;
    return checkIfNotOlderThan(value, typeof maxAge === 'number' ? maxAge : parseInt(maxAge, 10));
  },
  notLargerThan: (value: any, params?: { [key: string]: any }) => validateNotLargerThan(value, params),
  min: (value: any, params?: { [key: string]: any }) => {
    if (value === undefined || value === null || value === '') return true;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return !isNaN(num) && num >= (params?.minValue ?? 0);
  },
  /** Pass params.other = field name (string); valid if this value (array) or the other field's value (array) has length > 0 */
  atLeastOneOf: (value: any, params?: { [key: string]: any }) => {
    const a = Array.isArray(value) ? value.length : 0;
    const other = params?.other;
    const b = Array.isArray(other) ? other.length : 0;
    return a > 0 || b > 0;
  },
};

// Generiraj tip iz ključeva validationRules objekta
export type ValidationType = keyof typeof validationRules;

// Definiraj tip za validacijsko pravilo
export interface Validation {
  type: ValidationType;
  message: string;
  params?: { [key: string]: any };
}
